import { Divider } from '@byecode/ui'
import type { TypeInstanceMap, VariableADTvalue } from '@lighthouse/core'
import { Extension } from '@tiptap/core'
import { Placeholder } from '@tiptap/extensions'
import type { JSONContent } from '@tiptap/react'
import { EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import isEqual from 'fast-deep-equal'
import { max } from 'rambda'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useWindowSize } from 'react-use'
import { useImmer } from 'use-immer'

import { FieldPicker } from '../../FieldPicker'
import { VariableBlock } from '../../RichText'
import { VariableTagProps } from '../../Variable'
import { VariablePicker, VariablePickerProps } from '../../VariablePicker'
import { splitString } from './constants'
import { FormulaDefinitions, FormulaMode } from './FormulaDefinitions'
import { FormulaFooter } from './FormulaFooter'
import { CurrentValueMark } from './Mark/currentValue'
import * as SC from './styles'
import { FunctionObject, MethodList } from './types'
import { interceptFormulaReverseText, interceptFormulaText, replaceText } from './utils'

const FORMULA_TOKEN_END = new Set([...splitString, '(', '.', ')'])

interface FormulaEditorState {
    mode: FormulaMode
    selectFunction: FunctionObject | null
    functionList: MethodList[]
    range: [number, number] | []
    activeItem: FunctionObject | null
    innerType?: TypeInstanceMap
}
export interface FormulaEditorProps
    extends Pick<
            VariableTagProps,
            | 'pageOptions'
            | 'inputOption'
            | 'filterOption'
            | 'viewOptions'
            | 'upstreamOptions'
            | 'datasourceOption'
            | 'userOption'
            | 'dateOption'
            | 'fieldOptions'
            | 'formOption'
        >,
        Pick<VariablePickerProps, 'zIndex'> {
    enableOnlyField?: boolean
    content?: JSONContent
    methodList: MethodList[]
    innerTypeToMethods: Record<TypeInstanceMap, string[]>
    onChange: (content: JSONContent) => void
    onOk?: (json?: JSONContent) => void
    onClose?: () => void
}

export const FormulaEditor: React.FC<FormulaEditorProps> = ({
    content,
    methodList,
    innerTypeToMethods,
    enableOnlyField,
    onChange,
    onOk,
    onClose,
    ...rest
}) => {
    const methodRef = useRef<HTMLDivElement>(null)
    const { height } = useWindowSize()
    const [state, setState] = useImmer<FormulaEditorState>({
        mode: FormulaMode.all,
        selectFunction: null,
        functionList: methodList,
        range: [],
        activeItem: null
    })

    const { mode, functionList, range, activeItem, innerType } = state
    // const[state, ]
    const editTorMaxHeight = height - 600

    const stateRef = useRef(state)
    useEffect(() => {
        stateRef.current = state
    }, [state])

    const functions = useMemo(() => {
        return functionList.reduce<Record<string, FunctionObject>>((prev, item) => {
            item.list.forEach(item => {
                prev[item.name.toLocaleLowerCase()] = item
            })
            return prev
        }, {})
    }, [functionList])
    const functionsRef = useRef(functions)

    // const fieldsVariableOption = useMemo(() => {
    //     if (!fields) {
    //         return
    //     }
    //     return getFieldsVariableOption({ fields })
    // }, [fields])

    useEffect(() => {
        functionsRef.current = functions
    }, [functions])

    const setAllDefault = useCallback(() => {
        setState(draft => {
            draft.mode = FormulaMode.all
            draft.selectFunction = null
            draft.functionList = methodList
            draft.range = []
            draft.activeItem = methodList[0].list[0]
        })
    }, [methodList, setState])

    const setNone = useCallback(() => {
        setState(draft => {
            draft.mode = FormulaMode.none
            draft.innerType = undefined
            draft.range = []
            draft.activeItem = null
        })
    }, [setState])

    const setFunctionListMode = useCallback(
        (list: MethodList[], active?: FunctionObject | null) => {
            setState(draft => {
                draft.mode = FormulaMode.function
                draft.range = []
                draft.innerType = undefined
                draft.functionList = list
                draft.activeItem = active ?? list?.[0]?.list?.[0] ?? null
            })
        },
        [setState]
    )

    const scrollActiveItem = useCallback((id: string) => {
        const checkedDoms = document.querySelector(`[data-adapter='${id}']`)
        if (!checkedDoms || !methodRef.current) {
            return
        }
        const { scrollTop: scrollListTop, clientHeight } = methodRef.current
        const { y: parentY } = methodRef.current.getBoundingClientRect()

        const { y, height: itemHeight } = checkedDoms.getBoundingClientRect()
        const itemScrollTop = y - parentY + scrollListTop
        const scrollTop = itemScrollTop - clientHeight + itemHeight
        methodRef.current.scrollTo({ top: max(scrollTop, 0) })
    }, [])

    const handleHoverFunction = useCallback(
        (item: FunctionObject, isScroll = true) => {
            setState(draft => {
                draft.activeItem = item
            })
            isScroll && scrollActiveItem(item.id)
        },
        [scrollActiveItem, setState]
    )

    const anchorScroll = useCallback(
        (id: string) => {
            scrollActiveItem(id)
        },
        [scrollActiveItem]
    )

    const editor = useEditor(
        {
            autofocus: false,
            enableInputRules: true,
            enablePasteRules: true,
            extensions: [
                Placeholder.configure({
                    placeholder: '请输入公式'
                }),
                Extension.create({
                    addKeyboardShortcuts() {
                        return {
                            ArrowUp: e => {
                                return true
                            },
                            ArrowDown: () => {
                                return true
                            }
                        }
                    }
                }),
                CurrentValueMark,
                StarterKit.configure({
                    heading: false,
                    bulletList: false,
                    orderedList: false,
                    blockquote: false,
                    codeBlock: false,
                    horizontalRule: false
                }),
                VariableBlock.configure(rest)
            ],
            onUpdate({ editor, transaction }) {
                const json = editor.getJSON()
                onChange(json)
            },
            onSelectionUpdate({ editor }) {
                const {
                    view: {
                        state: { selection }
                    }
                } = editor
                const { $anchor } = selection
                const { nodeBefore, nodeAfter } = $anchor
                const latestState = stateRef.current
                const latestFunctions = functionsRef.current

                // 2) No node before => reset to default suggestions.
                if (!nodeBefore) {
                    setAllDefault()
                    return
                }

                const beforeText = nodeBefore.isText ? nodeBefore.text ?? '' : ''
                const afterText = nodeAfter?.isText ? nodeAfter.text ?? '' : ''
                // 3) Special characters near cursor.
                if (!beforeText) {
                    setAllDefault()
                    return
                }

                // Cursor inside empty parentheses (ignore spaces): `Filter(   )`
                // In this case, keep `activeItem` on the current function (`Filter`).
                const beforeTrimRight = beforeText.replace(/\s+$/g, '')
                const afterTrimLeft = afterText.replace(/^\s+/g, '')
                if (beforeTrimRight.endsWith('(') && afterTrimLeft.startsWith(')')) {
                    const beforeWithoutParen = beforeTrimRight.slice(0, -1)
                    const funcKey = interceptFormulaReverseText(
                        [...replaceText(beforeWithoutParen)].reverse().join(''),
                        FORMULA_TOKEN_END
                    ).toLocaleLowerCase()
                    const func = funcKey ? latestFunctions?.[funcKey] : undefined
                    if (func) {
                        setState(draft => {
                            draft.mode = FormulaMode.all
                            draft.activeItem = func
                            draft.functionList = methodList
                            draft.range = []
                        })
                        requestAnimationFrame(() => {
                            anchorScroll(func.id)
                        })
                        return
                    }
                }
                // dot or ")." => show methods for current innerType (if any)
                if (beforeText.endsWith('.')) {
                    const it = latestState.innerType
                    const filtered = methodList.filter(item => {
                        if (!it) {
                            return true
                        }
                        const functionTypeList = innerTypeToMethods[it]
                        return functionTypeList.includes(item.type)
                    })
                    setFunctionListMode(filtered)
                    return
                }
                if (beforeText.endsWith(')')) {
                    setNone()
                    return
                }

                // 4) Non-text node before => default.
                if (!nodeBefore.isText) {
                    setAllDefault()
                    return
                }

                const prevText = beforeText
                const prev = interceptFormulaReverseText(
                    [...replaceText(prevText)].reverse().join(''),
                    FORMULA_TOKEN_END
                ).toLocaleLowerCase()
                if (!prev) {
                    setAllDefault()
                    return
                }

                const next = afterText ? interceptFormulaText(afterText, FORMULA_TOKEN_END).toLocaleLowerCase() : ''
                const combined = prev + next
                const func = latestFunctions?.[combined]
                if (func) {
                    const method = methodList.find(item => item.type === func.type)
                    if (method) {
                        setFunctionListMode([{ type: method.type, name: method.name, list: [func] }], func)
                        anchorScroll(func.id)
                    }
                    return
                }

                // Search in current suggestion pool (not always full `methodList`).
                const searched = latestState.functionList.reduce<MethodList[]>((acc, group) => {
                    const list = group.list.filter(fn => fn.name.toLocaleLowerCase().includes(prev))
                    if (list.length > 0) {
                        acc.push({ ...group, list })
                    }
                    return acc
                }, [])
                if (searched.length > 0 && prev !== '()') {
                    setFunctionListMode(searched, searched[0].list[0])
                    return
                }

                const exact = latestFunctions?.[prev]
                if (exact) {
                    setState(draft => {
                        draft.mode = FormulaMode.all
                        draft.activeItem = exact
                        draft.functionList = methodList
                        draft.range = []
                    })
                    anchorScroll(exact.id)
                    return
                }
                setAllDefault()
            }
        },
        []
    )

    const handleSelect = useCallback(
        (val: VariableADTvalue) => {
            editor
                .chain()
                .focus()
                .insertContent({
                    type: 'variable',
                    attrs: {
                        value: val
                    }
                })
                .run()
        },
        [editor]
    )

    useEffect(() => {
        if (!editor || editor.isFocused) {
            return
        }
        const prevSelection = editor.state.selection
        const prevContent = editor.getJSON()
        if (!content || !prevContent || isEqual(prevContent, content)) {
            return
        }
        const editorContent = content.content
        if (editorContent) {
            editor.chain().setContent(editorContent, { emitUpdate: false }).setTextSelection(prevSelection).focus('end').run()
        }
    }, [content, editor])

    return (
        <SC.Container maxHeight={editTorMaxHeight} onClick={ev => ev.stopPropagation()}>
            <SC.Content>
                <SC.FormulaEditorHeader>
                    {enableOnlyField ? (
                        <FieldPicker {...rest} onSelect={handleSelect} />
                    ) : (
                        <VariablePicker {...rest} onSelect={handleSelect} />
                    )}
                </SC.FormulaEditorHeader>
                <Divider />
                <EditorContent className="formula-editor" editor={editor} />
            </SC.Content>
            <FormulaDefinitions
                ref={methodRef}
                mode={mode}
                methodList={methodList}
                functionList={functionList}
                activeItem={activeItem}
                editor={editor}
                onHoverFunction={handleHoverFunction}
            />
            <FormulaFooter value={content} onOk={onOk} onCancel={onClose} />
        </SC.Container>
    )
}
